<template>
    <div class="bg-gradient-to-br from-paletteYellow via-paletteOrange to-paletteDarkGreen overflow-auto">
        <!-- Hero Section -->
        <section class="py-16 min-h-[calc(100vh-4em)] flex flex-col justify-center">
            <div class="container mx-auto px-4">
                <div class="p-4 rounded-xl bg-[#FAF7F4] mb-8">
                    <img src="@/assets/logo.png" alt="PapoteAI" class="mx-auto mb-8 w-72 animate-wiggle">
                    <header class="text-center mb-12 text-paletteDarkGreen font-baloo2">
                        <h1 class="text-6xl font-bold mb-6">{{ $t('landingPage.title') }}</h1>
                        <p class="text-2xl max-w-2xl mx-auto">{{ $t('landingPage.subtitle') }}</p>
                    </header>
                </div>
                <div class="flex flex-col sm:flex-row gap-4 justify-center">
                    <router-link to="/register"
                        class="bg-white text-paletteDarkGreen font-bold py-3 px-8 rounded-full hover:bg-paletteYellow transition duration-300 ease-in-out shadow-lg hover:shadow-xl transform hover:-translate-y-1 inline-block">
                        {{ $t('landingPage.getFreeCreditsButton') }}
                    </router-link>
                    <button
                        class="bg-paletteDarkGreen text-white font-bold py-3 px-8 rounded-full hover:bg-paletteLightGreen transition duration-300 ease-in-out shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                        @click.prevent="scrollToSection('firstSection')">
                        {{ $t('landingPage.learnMoreButton') }}
                    </button>
                </div>
            </div>
        </section>

        <!-- Feature 1: Choose Scenario -->
        <section id="firstSection" class="py-20 min-h-[calc(100vh-4em)] flex flex-col justify-center">
            <div class="container mx-auto px-4">
                <div class="flex flex-col md:flex-row items-center gap-12">
                    <div class="md:w-1/2">
                        <img src="@/assets/landing-page/screenshot-scenario.png" alt="Choose scenario"
                            class="rounded-xl shadow-2xl w-full">
                    </div>
                    <div class="md:w-1/2">
                        <h2 class="text-4xl font-bold text-white mb-6">{{ $t('landingPage.feature1.title') }}</h2>
                        <p class="text-xl text-white/90 mb-6">{{ $t('landingPage.feature1.description') }}</p>
                    </div>
                </div>
                <div class="flex justify-end mt-8 gap-4">
                    <button
                        class="bg-white text-paletteDarkGreen font-bold py-2 px-6 rounded-full hover:bg-paletteYellow transition duration-300 ease-in-out shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                        @click.prevent="scrollToSection('feature2Section')">
                        {{ $t('landingPage.nextButton') }}
                    </button>
                </div>
            </div>
        </section>

        <!-- Feature 2: Chat Interface -->
        <section id="feature2Section" class="py-20 min-h-[calc(100vh-4em)] flex flex-col justify-center">
            <div class="container mx-auto px-4">
                <div class="flex flex-col md:flex-row-reverse items-center gap-12">
                    <div class="md:w-1/2">
                        <img src="@/assets/landing-page/screenshot-chat.png" alt="Chat interface"
                            class="rounded-xl shadow-2xl w-full">
                    </div>
                    <div class="md:w-1/2">
                        <h2 class="text-4xl font-bold text-white mb-6">{{ $t('landingPage.feature2.title') }}</h2>
                        <p class="text-xl text-white/90 mb-6">{{ $t('landingPage.feature2.description') }}</p>
                    </div>
                </div>
                <div class="flex justify-between mt-8 gap-4">
                    <button
                        class="bg-white text-paletteDarkGreen font-bold py-2 px-6 rounded-full hover:bg-paletteYellow transition duration-300 ease-in-out shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                        @click.prevent="scrollToSection('firstSection')">
                        {{ $t('landingPage.previousButton') }}
                    </button>
                    <button
                        class="bg-white text-paletteDarkGreen font-bold py-2 px-6 rounded-full hover:bg-paletteYellow transition duration-300 ease-in-out shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                        @click.prevent="scrollToSection('feature3Section')">
                        {{ $t('landingPage.nextButton') }}
                    </button>
                </div>
            </div>
        </section>

        <!-- Feature 3: AI Insights -->
        <section id="feature3Section" class="py-20 min-h-[calc(100vh-4em)] flex flex-col justify-center">
            <div class="container mx-auto px-4">
                <div class="flex flex-col md:flex-row items-center gap-12">
                    <div class="md:w-1/2">
                        <img src="@/assets/landing-page/screenshot-insights.png" alt="AI insights"
                            class="rounded-xl shadow-2xl w-full">
                    </div>
                    <div class="md>w-1/2">
                        <h2 class="text-4xl font-bold text-white mb-6">{{ $t('landingPage.feature3.title') }}</h2>
                        <p class="text-xl text-white/90 mb-6">{{ $t('landingPage.feature3.description') }}</p>
                    </div>
                </div>
                <div class="flex justify-between mt-8 gap-4">
                    <button
                        class="bg-white text-paletteDarkGreen font-bold py-2 px-6 rounded-full hover:bg-paletteYellow transition duration-300 ease-in-out shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                        @click.prevent="scrollToSection('feature2Section')">
                        {{ $t('landingPage.previousButton') }}
                    </button>
                    <button
                        class="bg-white text-paletteDarkGreen font-bold py-2 px-6 rounded-full hover:bg-paletteYellow transition duration-300 ease-in-out shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                        @click.prevent="scrollToSection('feature4Section')">
                        {{ $t('landingPage.nextButton') }}
                    </button>
                </div>
            </div>
        </section>

        <!-- Feature 4: User Feedback -->
        <section id="feature4Section" class="py-20 min-h-[calc(100vh-4em)] flex flex-col justify-center">
            <div class="container mx-auto px-4">
                <div class="flex flex-col md:flex-row-reverse items-center gap-12">
                    <div class="md:w-1/2">
                        <img src="@/assets/landing-page/screenshot-feedback.png" alt="User feedback"
                            class="rounded-xl shadow-2xl w-full">
                    </div>
                    <div class="md:w-1/2">
                        <h2 class="text-4xl font-bold text-white mb-6">{{ $t('landingPage.feature4.title') }}</h2>
                        <p class="text-xl text-white/90 mb-6">{{ $t('landingPage.feature4.description') }}</p>
                    </div>
                </div>
                <div class="flex justify-start mt-8 gap-4">
                    <button
                        class="bg-white text-paletteDarkGreen font-bold py-2 px-6 rounded-full hover:bg-paletteYellow transition duration-300 ease-in-out shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                        @click.prevent="scrollToSection('feature3Section')">
                        {{ $t('landingPage.previousButton') }}
                    </button>
                </div>
            </div>
        </section>

        <section class="py-20 min-h-[calc(100vh-4em)] flex flex-col justify-center">
            <div class="container mx-auto px-4 text-center">
                <h2 class="text-4xl font-bold text-white mb-8">{{ $t('landingPage.cta.title') }}</h2>
                <div class="flex flex-col items-center mb-8">
                    <p class="text-xl text-white/90 mb-2">{{ $t('landingPage.cta.description1') }}</p>
                    <p class="text-lg text-white/90">{{ $t('landingPage.cta.description2') }}</p>
                </div>
                <button
                    class="bg-white text-paletteDarkGreen font-bold py-4 px-12 rounded-full hover:bg-paletteYellow transition duration-300 text-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                    {{ $t('landingPage.cta.button') }}
                </button>
            </div>
        </section>

        <FooterComponent />
    </div>
</template>

<script setup>
import FooterComponent from '../layout/FooterComponent.vue'

function scrollToSection (sectionId) {
  const section = document.getElementById(sectionId)
  if (section) {
    section.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}
</script>

<style scoped>
.bg-gradient-to-br {
    background-size: 200% 200%;
    animation: gradient 15s ease infinite;
}

@keyframes gradient {
    0% {
        background-position: 0% 50%;
    }

    50% {
        background-position: 100% 50%;
    }

    100% {
        background-position: 0% 50%;
    }
}
</style>
